import { DateTime } from 'luxon'

const MONTH_FORMAT = 'yyyy-MM'
const DATE_FORMAT = 'yyyy-MM-dd'
const WINDOW_LABEL_FORMAT = 'LLL yyyy'

// The window runs from the first day of the month twelve months ago through
// the end of the current month, so it holds 13 month buckets: the 12 full
// months before this one plus the current (partial) month.
export const ROLLING_WINDOW_MONTHS_BACK = 12

const formatSignedDollars = (amountInDollars, formatAbsolute) => {
  const amount = amountInDollars || 0
  const formatted = formatAbsolute(Math.abs(amount))

  return amount < 0 && formatted !== '$0' ? `-${formatted}` : formatted
}

export const formatCurrency = amountInDollars =>
  formatSignedDollars(
    amountInDollars,
    amount => `$${Math.round(amount).toLocaleString()}`,
  )

export const formatCompactCurrency = amountInDollars =>
  formatSignedDollars(amountInDollars, amount =>
    amount >= 1000
      ? `$${Number((amount / 1000).toFixed(1))}k`
      : `$${Math.round(amount)}`,
  )

export const formatDecimal = value =>
  (value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })

export const formatPercent = value => `${formatDecimal(value)}%`

export const formatOptionalDecimal = value =>
  value == null ? '–' : formatDecimal(value)

export const formatOptionalPercent = value =>
  value == null ? '–' : formatPercent(value)

// The backend returns null for the benchmark fields (and gross earnings) unless
// its take-home flag is on for the coach.
export const hasTakeHome = benchmarks => benchmarks?.fullTimeBarDollars != null

export const formatFullTimeLabel = benchmarks =>
  hasTakeHome(benchmarks)
    ? `Progress toward ${formatCurrency(
      benchmarks.fullTimeBarDollars,
    )} full-time`
    : 'Progress toward full-time'

export const formatCount = (count, singular, plural = `${singular}s`) =>
  `${count.toLocaleString()} ${count === 1 ? singular : plural}`

// Orders ranked-list entries most to least by `getCount`, then by revenue
// (highest first), then by title A–Z. Returns a copy; the input is untouched.
export const sortByCountRevenueTitle = (
  entries,
  { getCount, getRevenue, getTitle },
) =>
  [...(entries || [])].sort(
    (a, b) =>
      (getCount(b) || 0) - (getCount(a) || 0) ||
      (getRevenue(b) || 0) - (getRevenue(a) || 0) ||
      (getTitle(a) || '').localeCompare(getTitle(b) || '', undefined, {
        sensitivity: 'base',
      }),
  )

export const formatEventPrice = priceInCents => {
  if (priceInCents == null) return null
  if (priceInCents === 0) return 'Free'

  const dollars = priceInCents / 100

  return `$${dollars.toLocaleString(undefined, {
    minimumFractionDigits: priceInCents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`
}

// e.g. "Oct 12, 2026 · 9:00 AM · $97", shown in the event's own timezone
export const formatEventDetail = ({
  startDateTime,
  timezone,
  priceInCents,
}) => {
  const start = DateTime.fromISO(startDateTime || '', {
    zone: timezone || undefined,
  })
  const parts = [
    start.isValid ? start.toFormat('LLL d, yyyy') : null,
    start.isValid ? start.toFormat('h:mm a') : null,
    formatEventPrice(priceInCents),
  ]

  return parts.filter(Boolean).join(' · ')
}

export const formatMonthLabel = (month, format = 'LLL yyyy') => {
  const parsed = DateTime.fromFormat(month || '', MONTH_FORMAT)

  return parsed.isValid ? parsed.toFormat(format) : month
}

export const getRollingWindow = (now = DateTime.now()) => {
  const start = now
    .minus({ months: ROLLING_WINDOW_MONTHS_BACK })
    .startOf('month')
  const end = now.endOf('month')
  const months = Array.from(
    { length: ROLLING_WINDOW_MONTHS_BACK + 1 },
    (_, index) => start.plus({ months: index }).toFormat(MONTH_FORMAT),
  )

  return {
    months,
    startDate: start.toFormat(DATE_FORMAT),
    endDate: end.toFormat(DATE_FORMAT),
    label: `${start.toFormat(WINDOW_LABEL_FORMAT)} – ${end.toFormat(
      WINDOW_LABEL_FORMAT,
    )}`,
  }
}

export const fitToMonths = (data, months, dataKey) =>
  months.map(month => ({
    month,
    [dataKey]:
      (data || []).find(point => point.month === month)?.[dataKey] || 0,
  }))

export const sortByMonth = data =>
  [...(data || [])].sort((a, b) => a.month.localeCompare(b.month))

export const getMonthOverMonth = (data, dataKey, now = DateTime.now()) => {
  const valueFor = month =>
    (data || []).find(point => point.month === month)?.[dataKey] || 0
  const current = valueFor(now.toFormat(MONTH_FORMAT))
  const previous = valueFor(now.minus({ months: 1 }).toFormat(MONTH_FORMAT))

  return {
    current,
    previous,
    deltaPercent:
      previous > 0 ? Math.round(((current - previous) / previous) * 100) : null,
  }
}

export const getMonthInsight = (
  { current, previous, deltaPercent },
  options,
) => {
  const { subject, format } = options

  if (!current && !previous) return null

  const thisMonth = `${subject} ${format(current)} so far this month`

  if (deltaPercent === null) return `${thisMonth}.`
  if (deltaPercent === 0) {
    return `${thisMonth}, even with last month (${format(previous)}).`
  }

  return `${thisMonth}, ${deltaPercent > 0 ? 'up' : 'down'} ${Math.abs(
    deltaPercent,
  )}% from ${format(previous)} last month.`
}

const MIX_CATEGORIES = [
  { id: 'private', label: 'Private', noun: 'Private lessons' },
  { id: 'group', label: 'Group', noun: 'Group events' },
]

export const sumMonthly = (byMonth, dataKey = 'count') =>
  (byMonth || []).reduce((sum, point) => sum + (point[dataKey] || 0), 0)

export const sumMonthlyCounts = byMonth => sumMonthly(byMonth, 'count')

export const buildMixSegments = ({ private: privateValue, group }) => {
  const values = { private: privateValue || 0, group: group || 0 }
  const total = values.private + values.group
  const privatePercent =
    total > 0 ? Math.round((values.private / total) * 100) : 0

  return {
    total,
    segments: MIX_CATEGORIES.map(({ id, label, noun }) => ({
      id,
      label,
      noun,
      value: values[id],
      percent:
        id === 'private'
          ? privatePercent
          : total > 0
            ? 100 - privatePercent
            : 0,
    })),
  }
}

export const getMixInsight = ({ segments, total }) => {
  if (!total) return null

  const [first, second] = segments
  if (first.value === second.value) {
    return `Bookings are split evenly between ${first.noun.toLowerCase()} and ${second.noun.toLowerCase()}.`
  }

  const leader = first.value > second.value ? first : second

  return `${leader.noun} account for ${leader.percent}% of bookings.`
}

export const getRevenueInsight = ({ segments, total }) => {
  if (!total) return null

  const [first, second] = segments
  if (first.value === second.value) {
    return `Revenue is split evenly between ${first.noun.toLowerCase()} and ${second.noun.toLowerCase()}.`
  }

  const leader = first.value > second.value ? first : second

  return `${leader.noun} account for ${leader.percent}% of revenue.`
}
